/**
 * /product-requirements-document — SEO landing page targeting the core
 * head term "product requirements document" / "what is a PRD" /
 * "prd meaning" / "prd document" cluster (≈2,300/mo, high intent, lots
 * of long-tail spillover).
 *
 * Dual job: (1) genuine guide value — tell a searcher plainly what a PRD
 * is and what a good one contains (Problem / Goals / Non-goals / User
 * stories / Requirements / Success metrics / Dependencies / Open
 * questions), so the page earns the ranking; (2) product
 * differentiation — a PRD in a Google Doc or Notion starts drifting the
 * moment it's written, because nothing links a decision to the
 * requirements that depend on it. Draftlize makes every decision in the
 * PRD an addressable card; change an upstream decision and dependents
 * auto-flag stale. Mentions "PRD template" and "decision log" in prose
 * (text only, no links) to catch adjacent intent.
 *
 * English-only + inline (no i18n), mirroring DecisionLog.tsx and the
 * comparison pages. Chinese variant lives in
 * ProductRequirementsDocumentZh.tsx.
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function ProductRequirementsDocument() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/product-requirements-document"
      documentTitle="PRD (Product Requirements Document): What It Is — and the Living Kind · Draftlize"
      documentDescription="PRD stands for product requirements document — the doc that states the problem, goals, non-goals, user stories, requirements, success metrics, dependencies, and open questions. The catch — a PRD in a doc starts drifting the moment it's written. Draftlize makes every decision in the PRD an addressable card, so dependents auto-flag stale when one changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · PRDs</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            What a PRD is —<br/>
            <em>and the kind that stays true.</em>
          </h1>
          <p className="lede">
            A <em>product requirements document</em> — a PRD — is the single document that says what you're building, for whom, and how you'll know it worked. Done well, it turns a vague intention into something a team and an engineer can act on without a meeting. This page does two things: it tells you plainly what a PRD is and what a good one contains, and then it's honest about the part nobody mentions — a PRD written in a Google Doc or Notion starts drifting the moment you publish it, because nothing in it connects a decision to the requirements that depend on it. Draftlize fixes that at the root.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">So, <em>what is a PRD?</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            A product requirements document is the source of truth for a feature or product: it states the problem you're solving, the outcome you want, and the specific behavior the thing must have. It exists so that the people building it don't have to reconstruct the intent from a Slack thread — the <em>PRD meaning</em>, stripped of ceremony, is simply <em>"the agreed answer to: what are we making, and why."</em>
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            It is not a spec for how to build it — that's an engineering design doc. A PRD is upstream of that: it defines the <em>what</em> and the <em>why</em> so the <em>how</em> has something to serve. A good PRD is short enough to read in one sitting and decisive enough that two readers walk away with the same picture. Length is not a virtue; a one-pager that makes the trade-offs explicit beats ten pages that hedge.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: 0 }}>
            The format is not sacred. Amazon writes a six-page narrative and a press release; a startup might write half a page in a wiki. What matters isn't the <em>PRD document</em> shape — it's whether the parts below are answered honestly. Below is what those parts are.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">The anatomy</span>
            <h2 className="serif">What a good PRD <em>contains.</em></h2>
          </div>
          <p>Eight parts. Skip a name if you don't need it, but answer the underlying question. This is the same skeleton whether you write it in a doc, copy it from a <a href="/prd-template" style={{ color: 'var(--amber)' }}>PRD template</a>, or build it in Draftlize — the next section is about what changes when each part stops being a paragraph and becomes a card.</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Problem',
              hint: 'Why bother',
              body: 'The user pain or business gap, stated before any solution. If you can\'t describe the problem without naming your feature, you\'re documenting a solution in search of one. This is the part that justifies the whole document.',
            },
            {
              k: 'Goals',
              hint: 'What success is',
              body: 'What this product must achieve, in plain terms. Three or fewer. Goals are the lens you use to cut scope later — anything that doesn\'t serve one is a candidate to drop.',
            },
            {
              k: 'Non-goals',
              hint: 'What you won\'t do',
              body: 'The tempting things you are deliberately not building, this time. The most underused section in any PRD, and the one that prevents the slow scope creep that sinks a release. Naming a non-goal is a decision, not an omission.',
            },
            {
              k: 'User stories',
              hint: 'Who, doing what',
              body: 'Concrete scenarios in the user\'s words — "as a PM, I want to cite a decision from a spec so I don\'t re-explain it." They keep the requirements honest by tying every one back to a person doing a thing.',
            },
            {
              k: 'Requirements',
              hint: 'The behavior',
              body: 'What the product must actually do, specific enough to build and test against. The body of the PRD. Each requirement should trace to a goal and a user story — a requirement that serves neither is decoration.',
            },
            {
              k: 'Success metrics',
              hint: 'How you\'ll know',
              body: 'The numbers that tell you it worked — activation, retention, task completion, whatever the goal implies. Decided up front, before the result can bias the choice. Without these, "did it work?" becomes a debate instead of a reading.',
            },
            {
              k: 'Dependencies',
              hint: 'What it rests on',
              body: 'The decisions, systems, and other work this PRD assumes. Pricing, an API that must exist, a decision made in another doc. In a static document this is a list you\'ll forget to revisit — and it\'s the part that decides whether the PRD stays true as things move.',
            },
            {
              k: 'Open questions',
              hint: 'Not yet decided',
              body: 'The things you don\'t know yet, named honestly instead of papered over. A PRD that pretends everything is settled is lying; one that lists its unknowns tells the reader exactly where the risk lives.',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          That's the anatomy. Below: why a PRD starts drifting the day you publish it — and what we do about it.
        </p>
      </section>

      <section className="compare" style={{ paddingTop: 8 }}>
        <div className="head">
          <div>
            <span className="label">Example</span>
            <h2 className="serif">A short PRD <em>example.</em></h2>
          </div>
          <p>The same eight parts, filled in for one small feature — a CSV export on a reports screen. Short on purpose: a good PRD is as long as the decision needs, not longer.</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: 'Problem', body: 'Users who need report data elsewhere copy rows into a spreadsheet by hand — slow, error-prone, and a recurring support complaint. There is no way to get the data out of the app.' },
            { k: 'Goals', body: '1) Any user can export the current report to CSV in one click. 2) The file matches what is on screen — filters, sorting, and visible columns. 3) A 10,000-row export finishes in under five seconds.' },
            { k: 'Non-goals', body: 'Scheduled or recurring exports, XLSX and PDF formats, and exporting beyond the current filtered view — all deliberately deferred to a later release.' },
            { k: 'User stories', body: 'As an analyst, I want to export the filtered report to CSV so I can pivot it in my own spreadsheet without re-keying the numbers.' },
            { k: 'Requirements', body: 'An Export button on the report toolbar; it exports the current filtered, sorted view with the visible columns; the CSV is UTF-8 with a header row; a toast confirms when the file is ready; exports over 50,000 rows are blocked with a clear message.' },
            { k: 'Success metrics', body: 'Copy-paste support tickets drop by half within 60 days; at least 20% of weekly report viewers use export within a month of launch.' },
            { k: 'Dependencies', body: 'The reports API must expose a filtered-export endpoint (platform team). Rests on the pricing decision that export ships on every paid plan.' },
            { k: 'Open questions', body: 'Do we include soft-deleted rows? Should very large exports move to an async, email-when-ready flow?' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 24, padding: '18px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <span className="serif" style={{ fontSize: 17, fontWeight: 500, color: 'var(--ink-0)', lineHeight: 1.2 }}>{f.k}</span>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p style={{ maxWidth: 720, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)', marginTop: 20 }}>
          Written in a doc, this example is accurate today and drifting tomorrow: change the pricing decision under <em>Dependencies</em> and nothing tells the requirements above that they now rest on a call that moved. In Draftlize each section is a card linked to the decisions it depends on, so an upstream change flags the dependent parts stale instead of leaving the example quietly wrong.
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why every PRD <em>drifts.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">It's most accurate the day you publish it</h3>
            <p>A PRD is a snapshot of decisions made at one moment. The product then moves — a price changes, a goal is dropped, an API ships differently than assumed. The document doesn't move with it. By the time anyone re-reads it, a real fraction is quietly wrong, and you can't tell which fraction, so the whole thing gets distrusted.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Nothing links a decision to what depends on it</h3>
            <p>You decide pricing in the Goals section. Three requirements downstream assume that price. Change the decision and those requirements are now wrong — but a Google Doc has no idea they're connected, so nobody updates them until a build breaks or a demo embarrasses you. The Dependencies section was supposed to catch this; it's prose, so it doesn't.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Keeping it current is a chore that loses</h3>
            <p>Maintaining a PRD competes with shipping, and shipping always wins. So the document becomes a record of what you thought on day one, not what's true now — and the team stops opening it, which defeats the entire reason it exists. A <a href="/decision-log" style={{ color: 'var(--amber)' }}>decision log</a> has the same fate for the same reason: discipline can't beat entropy by hand.</p>
          </div>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">A PRD doc vs <em>a living PRD.</em></h2>
          </div>
          <p>A PRD written in Notion or a doc is a static record. The fix isn't a better template or more discipline — it's a substrate that knows when a decision changed and tells everything that depended on it.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Google Doc / Notion / Confluence</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Each part of the PRD</td><td className="ours yes">A typed, addressable card</td><td className="partial">A heading and a paragraph</td></tr>
            <tr><td>Findable later</td><td className="ours yes">Citable by URL / ID from any spec</td><td className="no">Buried in a doc you grep for</td></tr>
            <tr><td>A decision changes — do dependents know?</td><td className="ours yes">They auto-flag stale</td><td className="no">No link; they silently rot</td></tr>
            <tr><td>Can an AI agent use it?</td><td className="ours yes">Native read + write via MCP</td><td className="partial">Reads flat text at best</td></tr>
            <tr><td>Who keeps it consistent</td><td className="ours yes">The substrate tracks it</td><td className="no">Your willpower</td></tr>
            <tr><td>Conflict between two requirements</td><td className="ours yes">Surfaced automatically</td><td className="no">Found in the next post-mortem</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">The same PRD, <em>made live.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Every decision is an addressable card</h3>
            <p>In Draftlize the PRD isn't one long document — each part is a typed card: a problem, a goal, a non-goal, a requirement, a success metric. Structured, stable ID, citable from a spec or a thread instead of buried in a wiki page. "Per decision c-04" becomes a real reference, not a vague memory.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Change one decision, dependents flag stale</h3>
            <p>Because dependencies are real links and not nouns in a list, changing an upstream decision turns every card that rested on it stale automatically — the way a build system invalidates everything downstream of a changed file. The promise the Dependencies section made, the substrate keeps. You see exactly what your change broke, the moment you make it.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">An AI agent reads and writes the PRD</h3>
            <p>Claude Code or Cursor, over MCP, reads every relevant decision before it drafts the next requirement, and writes new decisions back into the same shape. The PRD stops being a document for a reader who never returns and becomes context an agent consults on every turn — so what it captures is what's actually true, not what was true at launch.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          A PRD doesn't fail because it was written badly. It fails because nothing tells it when one of its decisions stopped being true.
        </blockquote>
        <cite>Keep the eight parts. Let the substrate keep them current.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is a PRD, in one sentence?', a: 'A product requirements document (PRD) is the agreed answer to "what are we building, for whom, and how will we know it worked" — it states the problem, goals, non-goals, user stories, requirements, success metrics, dependencies, and open questions, so a team can build without reconstructing the intent from a Slack thread.' },
            { q: 'Who writes the PRD?', a: 'The product manager owns it, but a good PRD is written with engineering, design, and whoever holds the business context. The PM is responsible for the decisions in it being clear and current — not for typing it alone.' },
            { q: 'What is the difference between a PRD and a technical spec?', a: 'A PRD defines the what and the why and is upstream; a technical (engineering design) spec defines the how and is downstream. The PRD says "users must be able to cite a decision from a spec"; the spec says how that citation is stored and rendered. One PRD often spawns several specs.' },
            { q: 'PRD vs BRD — what is the difference?', a: 'A business requirements document (BRD) captures the business need and success criteria — why the company should invest. A PRD translates that into what the product must actually do. The BRD justifies the bet; the PRD makes it buildable. Many teams fold both into one document.' },
            { q: 'How is a PRD in Draftlize different from one in Notion or a Google Doc?', a: 'In a doc, every part of the PRD is prose, and nothing links a decision to the requirements that depend on it — so it drifts silently the moment a decision changes. In Draftlize each part is a typed, addressable card; change an upstream decision and every dependent card auto-flags stale, and an AI agent can read and write the PRD over MCP instead of parsing flat text.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Write your first living PRD <em>free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Spin up a project, seed it from your existing docs, and let the agent draft the PRD as structured cards — then keep every decision, and every requirement that depends on it, current for you.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
      <TemplateCta template="prd" />
    </MarketingShell>
  );
}
